// Redux slice for projects state management
import { FeatureType } from '../../config/features';

const initialState = {
  projects: [],
  currentProject: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    limit: 20,
    skip: 0
  },
  // Track current project IDs for each feature to prevent duplication
  // Using FeatureType constants as keys for consistency with Zorro system
  featureProjectIds: {
    photoshoot: null,
    [FeatureType.INSTAGRAM_PHOTOSHOOT]: null,
    [FeatureType.PRODUCT_VIEW_GENERATOR]: null,
    [FeatureType.VARIATION_GENERATOR]: null,
    [FeatureType.MOODBOARD]: null,
    [FeatureType.GARMENT_EDITOR]: null,
    [FeatureType.TECHPACK]: null,
    [FeatureType.COLOR_CHANGER]: null,
    [FeatureType.REFABRIC]: null,
  }
};

// Action types
const FETCH_PROJECTS_START = 'projects/fetchProjectsStart';
const FETCH_PROJECTS_SUCCESS = 'projects/fetchProjectsSuccess';
const FETCH_PROJECTS_FAILURE = 'projects/fetchProjectsFailure';
const FETCH_PROJECT_START = 'projects/fetchProjectStart';
const FETCH_PROJECT_SUCCESS = 'projects/fetchProjectSuccess';
const FETCH_PROJECT_FAILURE = 'projects/fetchProjectFailure';
const CREATE_PROJECT_START = 'projects/createProjectStart';
const CREATE_PROJECT_SUCCESS = 'projects/createProjectSuccess';
const CREATE_PROJECT_FAILURE = 'projects/createProjectFailure';
const UPDATE_PROJECT_START = 'projects/updateProjectStart';
const UPDATE_PROJECT_SUCCESS = 'projects/updateProjectSuccess';
const UPDATE_PROJECT_FAILURE = 'projects/updateProjectFailure';
const DELETE_PROJECT_START = 'projects/deleteProjectStart';
const DELETE_PROJECT_SUCCESS = 'projects/deleteProjectSuccess';
const DELETE_PROJECT_FAILURE = 'projects/deleteProjectFailure';
const CLEAR_CURRENT_PROJECT = 'projects/clearCurrentProject';
const CLEAR_ERROR = 'projects/clearError';
const RESET_PROJECTS_STATE = 'projects/resetState';
const SET_FEATURE_PROJECT_ID = 'projects/setFeatureProjectId';
const CLEAR_FEATURE_PROJECT_ID = 'projects/clearFeatureProjectId';

// Action creators
export const fetchProjectsStart = () => ({
  type: FETCH_PROJECTS_START
});

export const fetchProjectsSuccess = (data) => ({
  type: FETCH_PROJECTS_SUCCESS,
  payload: data
});

export const fetchProjectsFailure = (error) => ({
  type: FETCH_PROJECTS_FAILURE,
  payload: error
});

export const fetchProjectStart = () => ({
  type: FETCH_PROJECT_START
});

export const fetchProjectSuccess = (project) => ({
  type: FETCH_PROJECT_SUCCESS,
  payload: project
});

export const fetchProjectFailure = (error) => ({
  type: FETCH_PROJECT_FAILURE,
  payload: error
});

export const createProjectStart = () => ({
  type: CREATE_PROJECT_START
});

export const createProjectSuccess = (project) => ({
  type: CREATE_PROJECT_SUCCESS,
  payload: project
});

export const createProjectFailure = (error) => ({
  type: CREATE_PROJECT_FAILURE,
  payload: error
});

export const updateProjectStart = () => ({
  type: UPDATE_PROJECT_START
});

export const updateProjectSuccess = (project, options = {}) => ({
  type: UPDATE_PROJECT_SUCCESS,
  payload: project,
  ...(options.updateActiveProject === false ? { meta: { updateActiveProject: false } } : {}),
});

export const updateProjectFailure = (error) => ({
  type: UPDATE_PROJECT_FAILURE,
  payload: error
});

export const deleteProjectStart = () => ({
  type: DELETE_PROJECT_START
});

export const deleteProjectSuccess = (projectId) => ({
  type: DELETE_PROJECT_SUCCESS,
  payload: projectId
});

export const deleteProjectFailure = (error) => ({
  type: DELETE_PROJECT_FAILURE,
  payload: error
});

export const clearCurrentProject = () => ({
  type: CLEAR_CURRENT_PROJECT
});

export const clearProjectsError = () => ({
  type: CLEAR_ERROR
});

export const resetProjectsState = () => ({
  type: RESET_PROJECTS_STATE
});

/**
 * Set the project ID for a specific feature to track active projects
 * 
 * @param {string} featureType - The feature type (e.g., 'photoshoot', 'productViewGenerator')
 * @param {string} projectId - The ID of the project currently being worked on
 */
export const setFeatureProjectId = (featureType, projectId) => ({
  type: SET_FEATURE_PROJECT_ID,
  payload: { featureType, projectId }
});

/**
 * Clear the project ID for a specific feature
 * 
 * @param {string} featureType - The feature type to clear the project ID for
 */
export const clearFeatureProjectId = (featureType) => ({
  type: CLEAR_FEATURE_PROJECT_ID,
  payload: featureType
});

// Reducer
const projectsReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch projects list
    case FETCH_PROJECTS_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case FETCH_PROJECTS_SUCCESS:
      // Sort projects by createdAt descending (latest first)
      const sortedProjects = [...(action.payload.projects || [])].sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_at || 0);
        const dateB = new Date(b.createdAt || b.created_at || 0);
        return dateB - dateA; // Descending order (newest first)
      });
      return {
        ...state,
        loading: false,
        projects: sortedProjects,
        pagination: action.payload.pagination || state.pagination,
        error: null
      };
    
    case FETCH_PROJECTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Fetch single project
    case FETCH_PROJECT_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case FETCH_PROJECT_SUCCESS:
      return {
        ...state,
        loading: false,
        currentProject: action.payload,
        error: null
      };
    
    case FETCH_PROJECT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Create project
    case CREATE_PROJECT_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case CREATE_PROJECT_SUCCESS:
      return {
        ...state,
        loading: false,
        projects: [action.payload, ...state.projects],
        currentProject: action.payload,
        error: null
      };
    
    case CREATE_PROJECT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Update project
    case UPDATE_PROJECT_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case UPDATE_PROJECT_SUCCESS:
      const updatedProjectId = action.payload.projectId || action.payload._id || action.payload.id;
      return {
        ...state,
        loading: false,
        projects: state.projects.map(p => {
          const existingId = p.projectId || p._id || p.id;
          return existingId === updatedProjectId ? action.payload : p;
        }),
        currentProject: action.meta?.updateActiveProject === false
          && (state.currentProject?.projectId || state.currentProject?._id || state.currentProject?.id) !== updatedProjectId
          ? state.currentProject : action.payload,
        error: null
      };
    
    case UPDATE_PROJECT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Delete project
    case DELETE_PROJECT_START:
      return {
        ...state,
        loading: true,
        error: null
      };
    
    case DELETE_PROJECT_SUCCESS:
      return {
        ...state,
        loading: false,
        projects: state.projects.filter(p => {
          const projectId = p.projectId || p._id || p.id;
          return projectId !== action.payload;
        }),
        currentProject: state.currentProject && 
                        (state.currentProject.projectId || state.currentProject._id || state.currentProject.id) === action.payload ? 
                        null : state.currentProject,
        error: null
      };
    
    case DELETE_PROJECT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload
      };
    
    // Other actions
    case CLEAR_CURRENT_PROJECT:
      return {
        ...state,
        currentProject: null
      };
    
    case CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    
    case RESET_PROJECTS_STATE:
      return initialState;
    
    case SET_FEATURE_PROJECT_ID:
      return {
        ...state,
        featureProjectIds: {
          ...state.featureProjectIds,
          [action.payload.featureType]: action.payload.projectId
        }
      };
    
    case CLEAR_FEATURE_PROJECT_ID:
      return {
        ...state,
        featureProjectIds: {
          ...state.featureProjectIds,
          [action.payload]: null
        }
      };
    
    default:
      return state;
  }
};

export default projectsReducer;